import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { ICentralChartContextValue, IYAxisCustomBound } from './types';
import { assertNotNil } from '../../../../utils/types';
import { useChartContext } from '../../ChartContext';
import { resetCentralChartSettingsAction } from '../../../../redux/slices/analyses';
import { useAppDispatch } from '../../../../redux/hooks';
import { defaultYAxisCustomBound } from './utils';
import { ICustomBound } from './types';
import { YAxisPosition } from '../../interfaces';
import { useImmer } from 'use-immer';
import { cloneDeep } from 'lodash';

interface IProps {
  children: ReactNode;
}

const CentralChartSettingContext =
  createContext<ICentralChartContextValue | null>(null);

export const useCentralChartSettingContext = () => {
  const context = useContext(CentralChartSettingContext);
  assertNotNil(context);
  return context;
};

export const CentralChartSettingProvider = ({ children }: IProps) => {
  const dispatch = useAppDispatch();
  const {
    metadata: { analysisId },
    settings: {
      chartSettings: { axisSettings },
    },
  } = useChartContext();

  const [isCentralChartSettingOpen, setIsCentralChartSettingOpen] =
    useState(false);

  // ====================== Start of Custom Bound ======================
  // Store input custom bound in Context, so that we could reset it as needed.
  // otherwise, reset chart setting will not affect on local state in YAxisBoundAssignment.tsx
  const getCustomBound = (yAxisPosition: YAxisPosition): IYAxisCustomBound => {
    const currentYAxisSettings = axisSettings.yAxisSettings[yAxisPosition];
    if (currentYAxisSettings.bounds?.min && currentYAxisSettings.bounds?.max) {
      return {
        min: currentYAxisSettings.bounds.min,
        max: currentYAxisSettings.bounds.max,
        isCustomApplied: true,
        errors: null,
      };
    }
    return defaultYAxisCustomBound;
  };

  const [customBound, setCustomBound] = useImmer<ICustomBound>({
    [YAxisPosition.LEFT]: getCustomBound(YAxisPosition.LEFT),
    [YAxisPosition.RIGHT]: getCustomBound(YAxisPosition.RIGHT),
  });
  // ====================== End of Custom Bound ======================

  // Hold the reset action in this context if we need to modify the internal logics
  // in this context. For example, reset to the state when it's opened instead of
  // the initial state.
  const onCentralChartSettingReset = useCallback(() => {
    dispatch(resetCentralChartSettingsAction({ analysisId }));
    setCustomBound((draft) => {
      draft[YAxisPosition.LEFT] = cloneDeep(defaultYAxisCustomBound);
      draft[YAxisPosition.RIGHT] = cloneDeep(defaultYAxisCustomBound);
    });
  }, [dispatch, analysisId, setCustomBound]);

  const contextValue = useMemo(
    () => ({
      isCentralChartSettingOpen,
      setIsCentralChartSettingOpen,
      onCentralChartSettingReset,
      customBound,
      setCustomBound,
    }),
    [
      isCentralChartSettingOpen,
      setIsCentralChartSettingOpen,
      onCentralChartSettingReset,
      customBound,
      setCustomBound,
    ],
  ) satisfies ICentralChartContextValue;

  return (
    <CentralChartSettingContext.Provider value={contextValue}>
      {children}
    </CentralChartSettingContext.Provider>
  );
};
